import { cn } from '@shadcn/lib/utils';
import { Checkbox } from '@shadcn/ui/checkbox';
import { cva } from 'class-variance-authority';
import React, { ReactNode } from 'react';

import { Label } from '../../atoms/label/label';

interface ICheckBoxProps extends Omit<React.ComponentProps<typeof Checkbox>, 'className'> {
  readonly className?: {
    checkbox?: string;
    label?: string;
    container?: string;
  };
  readonly label?: ReactNode;
  readonly required?: boolean;
  readonly variant?: 'default' | 'error';
}

const checkboxVariants = cva(['focus-visible:ring-primary-700', 'hover:cursor-pointer'], {
  variants: {
    variant: {
      default: [
        'border-neutral-400',
        'bg-neutral-0',
        'data-[state=checked]:border-neutral-400',
        'data-[state=checked]:text-neutral-700',
        'data-[state=checked]:bg-neutral-0',
      ],
      error: [
        'border-error',
        'bg-paper-light',
        'data-[state=checked]:border-error',
        'data-[state=checked]:text-paper-light',
        'data-[state=checked]:bg-error',
      ],
    },
    disabled: {
      true: [
        'border-neutral-900',
        'bg-neutral-500',
        'data-[state=checked]:border-neutral-0',
        'data-[state=checked]:text-neutral-0',
        'data-[state=checked]:bg-neutral-400',
      ],
    },
  },
  defaultVariants: {
    variant: 'default',
    disabled: false,
  },
});

const labelVariants = cva(['text-sm', 'text-teal-dark', 'font-normal', 'hover:cursor-pointer'], {
  variants: {
    disabled: {
      true: ['cursor-not-allowed', 'hover:cursor-not-allowed', 'opacity-50'],
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

const containerVariants = cva([], {
  variants: {
    hasLabel: {
      true: ['flex', 'items-center', 'gap-2'],
    },
  },
  defaultVariants: {
    hasLabel: false,
  },
});

function containerClassName(className: string | undefined, hasLabel: boolean) {
  return cn(containerVariants({ hasLabel }), className);
}
function checkboxClassName(className: string | undefined, variant: 'default' | 'error', disabled: boolean) {
  return cn(checkboxVariants({ variant, disabled }), className);
}
function labelClassName(className: string | undefined, disabled: boolean) {
  return cn(labelVariants({ disabled }), className);
}

function getAriaProps(required: boolean | undefined, labelId: string | undefined) {
  return {
    ...(required ? { 'aria-required': true } : {}),
    ...(labelId ? { 'aria-labelledby': labelId } : {}),
  };
}

export function CheckBox({
  label,
  variant = 'default',
  className,
  disabled,
  required,
  id: inputId,
  ...props
}: ICheckBoxProps) {
  const labelId = React.useId();

  const ariaProps = getAriaProps(required, label ? labelId : undefined);

  return (
    <div className={containerClassName(className?.container, Boolean(label))}>
      <Checkbox
        className={checkboxClassName(className?.checkbox, variant, Boolean(disabled))}
        disabled={disabled}
        id={inputId}
        {...ariaProps}
        {...props}
      />
      {label && (
        <Label className={labelClassName(className?.label, Boolean(disabled))} htmlFor={inputId} id={labelId}>
          {label}
        </Label>
      )}
    </div>
  );
}
